export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

const BASE = (import.meta.env.VITE_API_BASE_URL as string) || "/api";
const DEBUG_API = String(import.meta.env.VITE_DEBUG_API ?? "false") === "true";

function toPath(path: string): string {
  if (path.startsWith("http")) return path;
  if (path.startsWith("/api")) return path;
  return `${BASE.replace(/\/$/, "")}${path.startsWith("/") ? path : `/${path}`}`;
}

function redactForDebug(value: unknown): unknown {
  if (!value || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(redactForDebug);
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = /pwd|password|session_value/i.test(k) ? "[redacted]" : redactForDebug(v);
  }
  return out;
}

function logApiDebug(stage: string, payload: Record<string, unknown>) {
  if (!DEBUG_API) return;
  console.debug(`[api:${stage}]`, payload);
}

/**
 * Browser always uses fetch so MSW can intercept.
 * Vitest uses the same fetch path with setupServer, or falls back to in-process when configured.
 */
export async function request<T>(
  method: string,
  path: string,
  options?: { body?: unknown; query?: Record<string, string | number | boolean | undefined | null> },
): Promise<T> {
  const url = new URL(toPath(path), typeof window !== "undefined" ? window.location.origin : "http://localhost");
  if (options?.query) {
    for (const [k, v] of Object.entries(options.query)) {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
    }
  }
  logApiDebug("request", {
    method,
    url: url.toString(),
    body: redactForDebug(options?.body),
    query: options?.query,
  });

  // Vitest without MSW server: optional in-process bridge
  if (import.meta.env.MODE === "test" && import.meta.env.VITE_TEST_INPROCESS === "true") {
    const { handleMockRequest } = await import("@/mocks/router");
    const res = handleMockRequest({
      method,
      path: url.pathname,
      query: url.searchParams,
      body: options?.body,
    });
    if (res.status >= 400) {
      const b = res.body as { code?: string; message?: string };
      throw new ApiError(res.status, b.code || "ERROR", b.message || "请求失败", res.body);
    }
    return res.body as T;
  }

  const res = await fetch(url.toString(), {
    method,
    credentials: "include",
    headers: options?.body !== undefined ? { "Content-Type": "application/json; charset=utf-8" } : undefined,
    body: options?.body !== undefined ? JSON.stringify(options.body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  logApiDebug("response", {
    method,
    url: url.toString(),
    status: res.status,
    ok: res.ok,
    body: redactForDebug(json),
  });
  if (!res.ok) {
    console.error("[api:error]", {
      method,
      url: url.toString(),
      status: res.status,
      response: redactForDebug(json),
    });
    throw new ApiError(res.status, (json as { code?: string }).code || "ERROR", (json as { message?: string }).message || res.statusText, json);
  }
  return json as T;
}

export const http = {
  get: <T>(path: string, query?: Record<string, string | number | boolean | undefined | null>) =>
    request<T>("GET", path, { query }),
  post: <T>(path: string, body?: unknown, query?: Record<string, string | number | boolean | undefined | null>) =>
    request<T>("POST", path, { body, query }),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, { body }),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, { body }),
  delete: <T>(path: string) => request<T>("DELETE", path),
};
